/**
 * Scanner Transcription Panel
 * Live AI analysis of scanner audio using Tank AI
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAudio } from '@/contexts/AudioContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Mic,
  MicOff,
  Radio,
  AlertTriangle,
  Shield,
  Clock,
  Bot,
  Loader2,
  Volume2,
  Zap,
  Brain
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_COLORS = {
  critical: 'bg-red-500 text-white',
  urgent: 'bg-orange-500 text-white',
  routine: 'bg-blue-500 text-white',
  informational: 'bg-slate-500 text-white'
};

const TranscriptCard = ({ transcript }) => {
  const priorityClass = PRIORITY_COLORS[transcript.priority] || PRIORITY_COLORS.informational;
  
  return (
    <div className="p-3 rounded-lg border border-slate-700 bg-slate-800/50 mb-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-teal-400" />
          <span className="text-xs text-slate-400">{transcript.scanner_name}</span>
        </div>
        <Badge className={`text-[10px] ${priorityClass}`}>
          {transcript.priority}
        </Badge>
      </div>
      
      {/* Transcript Text */}
      <p className="text-sm text-slate-200 mb-3 italic">
        "{transcript.text}"
      </p>
      
      {/* Tank's Analysis */}
      {transcript.analysis && (
        <div className="p-2 rounded bg-teal-500/10 border border-teal-500/30">
          <div className="flex items-center gap-2 mb-1">
            <Bot className="w-3 h-3 text-teal-400" />
            <span className="text-[10px] font-medium text-teal-400">Tank's Analysis</span>
          </div>
          <p className="text-xs text-slate-300">{transcript.analysis.summary}</p>
          
          {transcript.analysis.tank_assessment && (
            <p className="text-xs text-teal-300 mt-2 font-medium">
              {transcript.analysis.tank_assessment}
            </p>
          )}
          
          {/* Detected Incidents */}
          {transcript.analysis.incidents?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {transcript.analysis.incidents.map((incident, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-orange-500/50 text-orange-400">
                  {incident.type}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Keywords */}
          {transcript.analysis.keywords?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {transcript.analysis.keywords.slice(0, 5).map((keyword, i) => (
                <span key={i} className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">
                  {keyword}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Timestamp */}
      <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-500">
        <Clock className="w-3 h-3" />
        {new Date(transcript.timestamp).toLocaleTimeString()}
      </div>
    </div>
  );
};

export const ScannerTranscriptionPanel = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const { currentScanner, isPlaying } = useAudio();
  const [transcripts, setTranscripts] = useState([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [autoTranscribe, setAutoTranscribe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const transcribeIntervalRef = useRef(null);

  // Fetch recent transcripts
  const fetchTranscripts = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/safety/transcripts/recent?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranscripts(data);
      }
    } catch (error) {
      console.error('Failed to fetch transcripts:', error);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/safety/transcripts/stats?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchTranscripts();
      fetchStats();
    }
  }, [isOpen, fetchTranscripts, fetchStats]);

  // Call real transcription API
  const transcribeNow = useCallback(async () => {
    if (!token || !currentScanner || !isPlaying) return;
    
    setLoading(true);
    
    try {
      // Get the stream URL for the current scanner
      const streamUrl = currentScanner.stream_url || currentScanner.streamUrl || currentScanner.url;
      
      if (!streamUrl) {
        console.error('No stream URL available for scanner');
        // Fallback to simulation if no URL
        await simulateTranscription();
        return;
      }
      
      // Call the live transcription API
      const response = await fetch(`${API_URL}/api/safety/transcribe/live`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scanner_id: currentScanner.id || currentScanner._id,
          scanner_name: currentScanner.name,
          stream_url: streamUrl,
          location: {
            lat: currentScanner.lat || currentScanner.location?.lat,
            lng: currentScanner.lng || currentScanner.location?.lng
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.transcript) {
          // Add to local state
          const newTranscript = {
            ...data.transcript,
            _id: data.transcript._id || Date.now().toString()
          };
          
          setTranscripts(prev => [newTranscript, ...prev].slice(0, 50));
        }
      } else {
        // If API fails, use simulation as fallback
        console.warn('Live transcription failed, using simulation');
        await simulateTranscription();
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      // Fallback to simulation
      await simulateTranscription();
    } finally {
      setLoading(false);
    }
  }, [token, currentScanner, isPlaying]);

  // Simulate transcription (fallback for demo/testing)
  const simulateTranscription = useCallback(async () => {
    if (!token || !currentScanner || !isPlaying) return;
    
    setLoading(true);
    
    try {
      // In production, you would capture audio chunks and send to the backend
      // For demo, we'll create a simulated transcript
      const simulatedTranscript = {
        scanner_id: currentScanner.id,
        scanner_name: currentScanner.name,
        location: {
          lat: currentScanner.lat || currentScanner.location?.lat,
          lng: currentScanner.lng || currentScanner.location?.lng
        },
        text: generateSimulatedDispatch(),
        timestamp: new Date().toISOString()
      };
      
      // Add to local state with simulated analysis
      const analysis = await analyzeWithTank(simulatedTranscript.text, currentScanner.name);
      
      const newTranscript = {
        ...simulatedTranscript,
        analysis,
        priority: analysis.priority || 'informational',
        _id: Date.now().toString()
      };
      
      setTranscripts(prev => [newTranscript, ...prev].slice(0, 50));
      
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentScanner, isPlaying]);

  // Generate simulated dispatch text for demo
  const generateSimulatedDispatch = () => {
    const dispatches = [
      "Unit 42, respond to a 10-50 at Main and 5th, possible injury accident.",
      "All units, be advised, we have a 10-31 in progress at 1200 Oak Street.",
      "Dispatch to medic 7, chest pains reported at 456 Elm Avenue, elderly male.",
      "Unit 15, traffic stop at Highway 101, license plate check requested.",
      "Fire dispatch, structure fire reported at 789 Pine Road, flames visible.",
      "Unit 23, domestic disturbance at 321 Maple Drive, caller reports yelling.",
      "All units, suspect vehicle last seen heading north on Interstate 5, dark sedan.",
      "Dispatch to rescue 3, water rescue needed at Lake Park, person in distress.",
    ];
    return dispatches[Math.floor(Math.random() * dispatches.length)];
  };

  // Simulate Tank AI analysis
  const analyzeWithTank = async (text, scannerName) => {
    // In production, this would call the backend API
    // For demo, we simulate the analysis
    const incidentTypes = ['accident', 'medical', 'fire', 'domestic', 'pursuit', 'traffic_stop'];
    const detectedType = incidentTypes[Math.floor(Math.random() * incidentTypes.length)];
    
    const priorities = ['critical', 'urgent', 'routine', 'informational'];
    const priority = text.includes('fire') || text.includes('10-31') ? 'critical' :
                    text.includes('injury') || text.includes('chest') ? 'urgent' :
                    priorities[Math.floor(Math.random() * 2) + 2];
    
    return {
      summary: `${detectedType.replace('_', ' ')} incident detected on ${scannerName}`,
      incidents: [{ type: detectedType, description: text.substring(0, 50) }],
      priority,
      keywords: text.toLowerCase().split(' ').filter(w => w.length > 3).slice(0, 5),
      tank_assessment: priority === 'critical' ? 
        "High priority incident - multiple units may be needed." :
        priority === 'urgent' ?
        "Elevated concern - monitor for escalation." :
        "Routine activity - standard procedures apply."
    };
  };

  // Toggle auto-transcription
  const toggleAutoTranscribe = (enabled) => {
    setAutoTranscribe(enabled);
    
    if (enabled && isPlaying) {
      // Start interval for auto-transcription (every 30 seconds)
      transcribeIntervalRef.current = setInterval(transcribeNow, 30000);
      transcribeNow(); // Do one immediately
    } else {
      if (transcribeIntervalRef.current) {
        clearInterval(transcribeIntervalRef.current);
        transcribeIntervalRef.current = null;
      }
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (transcribeIntervalRef.current) {
        clearInterval(transcribeIntervalRef.current);
      }
    };
  }, []);

  // Stop auto-transcribe when scanner stops
  useEffect(() => {
    if (!isPlaying && autoTranscribe) {
      toggleAutoTranscribe(false);
    }
  }, [isPlaying]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[420px] p-0 glass-card border-slate-800">
        <SheetHeader className="sr-only">
          <SheetTitle>Tank AI Transcription</SheetTitle>
          <SheetDescription>Live AI analysis of scanner audio</SheetDescription>
        </SheetHeader>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-teal-400" />
                Tank AI Transcription
              </h3>
              {isTranscribing && (
                <Badge className="bg-red-500 text-white animate-pulse">
                  <Mic className="w-3 h-3 mr-1" />
                  LIVE
                </Badge>
              )}
            </div>

            {/* Current Scanner */}
            {currentScanner && isPlaying ? (
              <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/30 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Volume2 className="w-4 h-4 text-teal-400" />
                  <span className="text-sm font-medium text-white">{currentScanner.name}</span>
                </div>
                <p className="text-xs text-slate-400">
                  {currentScanner.city}, {currentScanner.state}
                </p>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 mb-4">
                <p className="text-sm text-slate-400 text-center">
                  Play a scanner to enable transcription
                </p>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={autoTranscribe}
                  onCheckedChange={toggleAutoTranscribe}
                  disabled={!isPlaying}
                  data-testid="auto-transcribe-switch"
                />
                <span className="text-xs text-slate-400">Auto-transcribe</span>
              </div>
              
              <Button
                size="sm"
                onClick={transcribeNow}
                disabled={!isPlaying || loading}
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="transcribe-now-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-1" />
                    Transcribe Now
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="px-4 py-3 border-b border-slate-700">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 rounded bg-slate-800/50">
                  <p className="text-lg font-bold text-white">{stats.total_transcripts || 0}</p>
                  <p className="text-[10px] text-slate-400">Total</p>
                </div>
                <div className="text-center p-2 rounded bg-slate-800/50">
                  <p className="text-lg font-bold text-red-400">
                    {(stats.by_priority?.critical || 0) + (stats.by_priority?.urgent || 0)}
                  </p>
                  <p className="text-[10px] text-slate-400">Critical</p>
                </div>
                <div className="text-center p-2 rounded bg-slate-800/50">
                  <p className="text-lg font-bold text-teal-400">
                    {Object.keys(stats.by_incident_type || {}).length}
                  </p>
                  <p className="text-[10px] text-slate-400">Types</p>
                </div>
              </div>
            </div>
          )}

          {/* Transcripts */}
          <ScrollArea className="flex-1 p-4">
            {transcripts.length > 0 ? (
              transcripts.map(transcript => (
                <TranscriptCard key={transcript._id} transcript={transcript} />
              ))
            ) : (
              <div className="text-center py-8">
                <Mic className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No transcripts yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  Play a scanner and click "Transcribe Now"
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Floating button for quick access
export const TranscriptionFloatingButton = ({ onClick }) => {
  const { currentScanner, isPlaying } = useAudio();
  
  if (!isPlaying) return null;
  
  return (
    <Button
      onClick={onClick}
      className="fixed bottom-24 right-4 z-50 rounded-full w-12 h-12 bg-teal-600 hover:bg-teal-500 shadow-lg shadow-teal-500/30"
      title="Tank AI Transcription"
      data-testid="transcription-floating-btn"
    >
      <Brain className="w-5 h-5" />
    </Button>
  );
};

export default ScannerTranscriptionPanel;
